import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Form, Select, Button, DatePicker, Empty, Tooltip } from 'antd'
import { downloadVehicleReport, fetchLastWeekData, fetchVehicles, verifyToken } from '../../utils/helpers'
import ReportDrawer from '../../Components/ReportDrawer/ReportDrawer'
import { dateFormate, translations, pageSizeOptions } from '../../utils/constants'
import { MdLogin, MdLogout, MdPersonOutline } from "react-icons/md"
import { secureApi } from '../../Config/api'
import { POST } from '../../utils/apis'
import moment from 'moment'
import { setFilteredVehicles, clearFilteredVehicles } from '../../Redux/actions/filterVehiclesAction'
import { setTotalVehicles, setVechiclesData } from '../../Redux/actions/vehiclesAction'
import { elmecLogo } from '../../assets/index'
import { ArrowDownToLine, Filter, FileText } from 'lucide-react'

const Reports = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(state => state?.authReducer?.user)
  const filteredVehicles = useSelector(state => state?.filterVehicleReducer?.filteredVehicles)
  const organizationDetails = useSelector(state => state?.authReducer?.organization)
  const vehicles = useSelector(state => state.vehiclesReducer?.vehicles)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [filters, setFilters] = useState({})
  const [dateRange, setDateRange] = useState([])
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [totalSummary, setTotalSummary] = useState([])
  const [organizationId, setOrganizationId] = useState(organizationDetails?.id)
  // console.log(filters)
  const { RangePicker } = DatePicker
  const elmecLogoFullPath = `${window.location.origin}${elmecLogo}`;


  useEffect(() => {
    verifyToken(history, dispatch)
    fetchLastWeekData(form, dispatch, setFilteredVehicles, setFilters, setTotalSummary, setDateRange, user, organizationId)
    fetchVehicles({ setTotalVehicles, setVechiclesData, dispatch, user, organizationId })
    dispatch(setFilteredVehicles(vehicles))
  }, [])


  const onApplyFilters = async () => {
    try {
      dispatch(setFilteredVehicles([]))
      const values = await form.validateFields()
      const { dateRange, vehicle } = values

      const formattedDateRange = dateRange
        ? dateRange.map(date => date.format(dateFormate))
        : null

      if ((!formattedDateRange || formattedDateRange?.length === 0) && (!vehicle || vehicle?.length === 0)) {
        fetchLastWeekData(form, dispatch, setFilteredVehicles, setFilters, setTotalSummary, setDateRange, user, organizationId)
        return
      }

      const response = await secureApi?.post(POST?.REPORT_FILTER, {
        dateRange: formattedDateRange,
        vehicleIds: vehicle,
        organizationId: organizationId,
        userId: user?.id,
      })

      if (response?.success) {
        let sortedData = response?.data?.records?.sort((a, b) => {
          const dateComparison = new Date(a?.created_at) - new Date(b?.created_at)
          if (dateComparison === 0) {
            return a?.vehicle_name.localeCompare(b?.vehicle_name)
          }
          return dateComparison
        })

        dispatch(setFilteredVehicles({ ...response?.data, records: sortedData || [] }))
        setFilters(response?.data)
        setTotalSummary(response?.data?.totalSummary)
      }
      else if (response?.success === false) {
        message.error(response?.error || response?.message)
      }
    } catch (error) {
      console.error('Error applying filters:', error)
    }
  }

  const onClearFilters = () => {
    form?.resetFields()
    setFilters({})
    dispatch(clearFilteredVehicles())
    dispatch(setFilteredVehicles(vehicles))
    fetchLastWeekData(form, dispatch, setFilteredVehicles, setFilters, setTotalSummary, setDateRange, user, organizationId)
  }

  // const onSelectChange = (newSelectedRowKeys) => {
  //   console.log('Selected row keys:', newSelectedRowKeys)
  //   setSelectedRowKeys(newSelectedRowKeys)
  // }

  const data = useMemo(() => {
    return (filteredVehicles?.records || [])?.map((vehicle, index) => ({
      key: `${vehicle?.id}-${index}`,
      date: moment(vehicle?.created_at).format("DD/MM/YYYY"),
      vehicleName: vehicle?.vehicle_name,
      enter: vehicle?.total_enter,
      exit: vehicle?.total_exit,
      remaining: vehicle?.remaining_people,
      organizationName: vehicle?.organization_name
    }))
  }, [filteredVehicles])


  const columns = [
    {
      title: translations[language]?.dateTxtReport,
      dataIndex: 'date',
      key: 'date',
      render: (text) => (text === undefined || text === null) ? '-' : text
    },
    ...(user?.role === 'superAdmin' ? [
      {
        title: translations[language]?.organizations,
        dataIndex: 'organizationName',
        key: 'organizationName',
        render: (text) => (
          <span className='capitalized-name'>{text || '-'}</span>
        ),
      }
    ] : []),
    {
      title: translations[language]?.vehicleName,
      dataIndex: 'vehicleName',
      key: 'vehicleName',
      render: (text) => (
        <span className='rp-vehicle capitalized-name'>{text || '-'}</span>
      )
    },
    {
      title: translations[language]?.txtEnterPassengers,
      dataIndex: 'enter',
      key: 'enter',
      align: 'center',
      render: (text) => <span className='rp-num rp-num-enter'>{(text === undefined || text === null || text === '') ? 0 : text}</span>
    },
    {
      title: translations[language]?.txtExitPassengers,
      dataIndex: 'exit',
      key: 'exit',
      align: 'center',
      render: (text) => <span className='rp-num rp-num-exit'>{(text === undefined || text === null || text === '') ? 0 : text}</span>
    },
    {
      title: translations[language]?.txtTotalPassengers,
      dataIndex: 'remaining',
      key: 'remaining',
      align: 'center',
      render: (text) => <span className='rp-num rp-num-total'>{(text === undefined || text === null || text === '') ? 0 : text}</span>
    },
    {
      title: translations[language]?.getReport,
      key: 'getReport',
      align: 'center',
      width: 110,
      render: (_, record) => (
        <Tooltip title={translations[language]?.getReport}>
          <button
            type='button'
            className='vl-action-btn vl-action-edit'
            onClick={() =>
              downloadVehicleReport({
                records: {
                  date: record.date,
                  vehicleName: record.vehicleName,
                  organizationName: record.organizationName,
                  enter: record.enter,
                  exit: record.exit,
                  remaining: record.remaining
                },
                elmecLogoImage: organizationDetails?.logo || elmecLogoFullPath,
                isSuperAdmin: user?.role === 'superAdmin',
                language,
                organizationDetails,
                user,
              })
            }
          >
            <ArrowDownToLine size={15} />
          </button>
        </Tooltip>
      )
    },
  ]

  const summaryCards = [
    { key: 'enter', value: totalSummary?.total_entries || 0, label: translations[language]?.totalEnter, color: '#00E096', icon: <MdLogin size={22} color='white' /> },
    { key: 'exit', value: totalSummary?.total_exits || 0, label: translations[language]?.totalExit, color: '#1976D2', icon: <MdLogout size={22} color='white' /> },
    { key: 'total', value: totalSummary?.total_remaining || 0, label: translations[language]?.totalPassengers, color: '#B282D6', icon: <MdPersonOutline size={22} color='white' /> },
  ]

  const periodLabel = dateRange?.length === 2 && dateRange[0] && dateRange[1]
    ? `${moment(dateRange[0]).format(dateFormate)} - ${moment(dateRange[1]).format(dateFormate)}`
    : null

  // const rowSelection = {
  //   selectedRowKeys,
  //   onChange: onSelectChange,
  // }

  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <div className='insider-container rp-page'>
          <div className='insider-container-buttons report-header-div rp-header'>
            <div>
              <h1 className='main-heading margin-bottom-0'>{translations[language]?.reports}</h1>
              <p className='rp-header-sub'>{translations[language]?.reportsSubtitle}</p>
            </div>
            <div className='get-reports-container'>
              <Button
                loading={downloadLoading}
                type='primary'
                className='add-button'
                onClick={() =>
                  downloadVehicleReport({
                    records: data,
                    elmecLogoImage: organizationDetails?.logo || elmecLogoFullPath,
                    isSuperAdmin: user?.role === 'superAdmin',
                    language,
                    organizationDetails,
                    user,
                    setDownloadLoading
                  })
                }
              >
                {translations[language]?.getReport}
                <ArrowDownToLine style={{ height: '18px' }} className='icons' />
              </Button>

            </div>
          </div>
          <div className='vd-card rp-filters'>
            <div className='vd-card-header'>
              <h2 className='vd-card-title'>
                <Filter size={16} className='rp-title-icon' />
                {translations[language]?.filtersLabel}
              </h2>
              <div className='rp-filter-actions'>
                <Button className='report-clear-btn' onClick={onClearFilters}>
                  {translations[language]?.clearText}
                </Button>
                <Button className='report-apply-btn' type='primary' onClick={onApplyFilters}>
                  {translations[language]?.applyTxt}
                </Button>
              </div>
            </div>
            <div className='vd-card-body'>
            <Form form={form} layout='vertical' className='filter-form rp-filter-form'>
              <Form.Item className='filter-input' name='dateRange' label={translations[language]?.dateRange}>
                <RangePicker
                  className='ip-address-date-picker'
                  format={dateFormate}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  value={dateRange?.length ? dateRange : null}
                  onChange={(dates) => {
                    setDateRange(dates)
                    form.setFieldsValue({ dateRange: dates })
                  }}
                  placeholder={[
                    translations[language]?.startDate,
                    translations[language]?.endDate
                  ]}
                />
              </Form.Item>
              <Form.Item name="vehicle" label={translations[language]?.vechiclelabel} className="report-input filter-input">
                <Select
                  className='report-select-dropdown mac-address-people-counter'
                  mode="multiple"
                  style={{ display: 'flex', width: '100%' }}
                  placeholder={translations[language]?.enterVehicle}
                  options={vehicles?.map((vehicle) => ({
                    label: vehicle?.vehicle_name,
                    value: vehicle.id,
                  }))}
                  filterOption={(input, option) =>
                    option?.label.toLowerCase()?.includes(input?.toLowerCase())}
                />
              </Form.Item>

            </Form>
            </div>
          </div>
          <div className='vd-stats rp-stats'>
            {summaryCards.map((card) => (
              <div className='vd-stat' key={card.key}>
                <div className='vd-stat-icon' style={{ backgroundColor: card.color }}>{card.icon}</div>
                <div>
                  <p className='vd-stat-value'>{card.value}</p>
                  <p className='vd-stat-label'>{card.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className='vd-card rp-table-card'>
            <div className='vd-card-header'>
              <div className='vl-list-title'>
                <h2 className='vd-card-title'>
                  <FileText size={16} className='rp-title-icon' />
                  {translations[language]?.passengerReportsTitle}
                </h2>
                <span className='vl-total-chip'>
                  {translations[language]?.totalRecord}: <strong>{data?.length || 0}</strong>
                </span>
              </div>
              {periodLabel && <span className='vd-card-meta'>{periodLabel}</span>}
            </div>
            <div className='vd-card-body'>
              <Table
                className='vl-table'
                rowKey={(r, i) => `${r?.date}-${r?.vehicleName}-${i}`}
                dataSource={data}
                columns={columns}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions,
                  defaultPageSize: 10
                }}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div className='rp-empty'>
                          <p className='rp-empty-title'>{translations[language]?.noReportData}</p>
                          <p className='rp-empty-hint'>{translations[language]?.noReportDataHint}</p>
                        </div>
                      }
                    />
                  ),
                }}
              />
            </div>
          </div>
          <ReportDrawer
            isVisible={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            title={translations[language]?.generateReport}
            language={language}
          />
        </div>
      </div>
    </div>
  )
}

export default Reports